import { useState } from "react";

import { Button } from "@/components/ui/button";

import type { EntryDetailView } from "./entryDetailAdapter";

/**
 * The "perfect review-brief" presentational body, reused across:
 * - the HITL review row (`ReviewRow.tsx`)
 * - the catalogue expand-on-click row
 * - the drafts expand-on-click row
 *
 * Lifted into a shared component per issue #218 — the operator-feedback
 * rationale (raw-JSON toggle, full embedding-text surface,
 * three-callsite reuse) sits there.
 *
 * Layout (mirrors the original `ReviewRow` body that operator feedback
 * called "perfect"):
 *   1. Two-column `<dl>` grid — left side carries identification +
 *      curator metadata, right side carries validator metadata.
 *   2. Full-width Embedding text section — the curator's own
 *      description of what the tool does and how, in a bordered box so
 *      long prose stays legible.
 *   3. Auxiliary metadata block — surfaced only when at least one
 *      field is non-null (integrations, company-size fit, verified,
 *      last_updated, source URLs, status, notes).
 *   4. Drafts-only staging fields (`discoverySource`, `curatorNotes`)
 *      — surfaced only when present.
 *   5. "Show raw JSON" toggle — pretty-prints the unmodified API row.
 *
 * The component is purely presentational: pass in the normalised view
 * via {@link EntryDetailView} and the raw row for the JSON toggle.
 */
export interface EntryDetailProps {
  view: EntryDetailView;
  /** The original API row — rendered verbatim in the "Show raw JSON" toggle. */
  raw: unknown;
}

export function EntryDetail({ view, raw }: EntryDetailProps) {
  const [showJson, setShowJson] = useState(false);

  const hasAuxMetadata =
    view.integrations !== null ||
    view.companySizeFit !== null ||
    view.verified !== null ||
    view.lastUpdated !== null ||
    view.sourceUrl !== null ||
    view.sourceUrls !== null ||
    view.status !== null ||
    view.notes !== null;

  const hasDraftFields =
    view.discoverySource !== null || view.curatorNotes !== null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <dl className="space-y-1.5 text-sm">
          <Field label="Category">
            {view.category || "—"}
            {view.subcategory ? (
              <span className="text-text-muted"> · {view.subcategory}</span>
            ) : null}
          </Field>
          <Field label="Problem tags">
            {view.problemTags.length > 0 ? view.problemTags.join(", ") : "—"}
          </Field>
          <Field label="Pricing">
            {view.pricingModel || "—"}
            {view.pricingFrom !== null ? (
              <span className="text-text-muted">
                {view.pricingFrom === -1
                  ? " · contact sales"
                  : ` · from $${view.pricingFrom}`}
              </span>
            ) : null}
          </Field>
          <Field label="Confidence">
            {view.confidenceScore !== null ? view.confidenceScore.toFixed(2) : "—"}
            <span className="text-text-muted">
              {" "}·{" "}
              {view.sourceCount !== null
                ? `${view.sourceCount} sources`
                : "no source count"}
            </span>
          </Field>
        </dl>

        <dl className="space-y-1.5 text-sm">
          <Field label="Quality score">
            {view.qualityScore !== null ? (
              <span className="font-mono">{view.qualityScore.toFixed(2)} / 5.00</span>
            ) : (
              "—"
            )}
          </Field>
          <Field label="Quality breakdown">
            <QualityBreakdown view={view} />
          </Field>
          <Field label="Validator flag">
            {view.validatorFlag === null
              ? "—"
              : view.validatorFlag
                ? "TRUE"
                : "FALSE"}
          </Field>
          <Field label="Quality notes">
            <span className="whitespace-pre-line">{view.qualityNotes ?? "—"}</span>
          </Field>
          {view.qualitySources !== null ? (
            <Field label="Quality sources">
              <span className="whitespace-pre-line">{view.qualitySources}</span>
            </Field>
          ) : null}
        </dl>
      </div>

      <section>
        <h4 className="label-mono mb-1.5">Embedding text</h4>
        <div className="rounded border border-border-light bg-bg p-3 text-sm whitespace-pre-wrap">
          {view.embeddingText ?? (
            <span className="text-text-muted">— (no embedding text recorded)</span>
          )}
        </div>
      </section>

      {hasAuxMetadata ? (
        <section>
          <h4 className="label-mono mb-1.5">Metadata</h4>
          <dl className="grid grid-cols-1 gap-1.5 text-sm md:grid-cols-2">
            {view.integrations !== null ? (
              <Field label="Integrations">{view.integrations}</Field>
            ) : null}
            {view.companySizeFit !== null ? (
              <Field label="Company size fit">{view.companySizeFit}</Field>
            ) : null}
            {view.verified !== null ? (
              <Field label="Verified">{view.verified}</Field>
            ) : null}
            {view.lastUpdated !== null ? (
              <Field label="Last updated">{view.lastUpdated}</Field>
            ) : null}
            {view.status !== null ? (
              <Field label="Status">{view.status}</Field>
            ) : null}
            {view.sourceUrl !== null ? (
              <Field label="Source URL">
                <ExternalLink href={view.sourceUrl} />
              </Field>
            ) : null}
            {view.sourceUrls !== null ? (
              <Field label="Source URLs">
                <span className="whitespace-pre-line">{view.sourceUrls}</span>
              </Field>
            ) : null}
            {view.notes !== null ? (
              <Field label="Notes">
                <span className="whitespace-pre-line">{view.notes}</span>
              </Field>
            ) : null}
          </dl>
        </section>
      ) : null}

      {hasDraftFields ? (
        <section>
          <h4 className="label-mono mb-1.5">Curator staging</h4>
          <dl className="space-y-1.5 text-sm">
            {view.discoverySource !== null ? (
              <Field label="Discovery source">{view.discoverySource}</Field>
            ) : null}
            {view.curatorNotes !== null ? (
              <Field label="Curator notes">
                <span className="whitespace-pre-line">{view.curatorNotes}</span>
              </Field>
            ) : null}
          </dl>
        </section>
      ) : null}

      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowJson((v) => !v)}
          aria-pressed={showJson}
        >
          {showJson ? "Hide raw JSON" : "Show raw JSON"}
        </Button>
        {showJson ? (
          <pre className="mt-2 max-h-96 overflow-auto rounded border border-border-light bg-bg p-3 text-xs font-mono text-text">
            {JSON.stringify(raw, null, 2)}
          </pre>
        ) : null}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="label-mono">{label}</dt>
      <dd className="text-text">{children}</dd>
    </div>
  );
}

/**
 * Renders the three validator sub-scores when any are present, comma-
 * separated. Collapses to `—` when none recorded — common for review
 * batch items where the breakdown isn't surfaced on the contract.
 */
function QualityBreakdown({ view }: { view: EntryDetailView }) {
  const parts: string[] = [];
  if (view.qualityTagsScore !== null) parts.push(`tags ${view.qualityTagsScore.toFixed(2)}`);
  if (view.qualityEmbeddingScore !== null)
    parts.push(`embedding ${view.qualityEmbeddingScore.toFixed(2)}`);
  if (view.qualityConfidenceScore !== null)
    parts.push(`confidence ${view.qualityConfidenceScore.toFixed(2)}`);
  if (parts.length === 0) return <span className="text-text-muted">—</span>;
  return <span className="font-mono text-xs">{parts.join(" · ")}</span>;
}

function ExternalLink({ href }: { href: string }) {
  const safe = isHttpUrl(href);
  if (!safe) return <span>{href}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-accent hover:underline"
    >
      {href}
    </a>
  );
}

function isHttpUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}
